import { ref, computed, readonly } from 'vue'
import { defineStore } from 'pinia'
import {
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  signInWithEmailAndPassword,
  signInWithCustomToken as fbSignInWithCustomToken,
  signOut as fbSignOut,
  onIdTokenChanged,
  TotpMultiFactorGenerator,
  type AuthError,
  type IdTokenResult,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type TotpSecret,
  type User as FbUser,
} from 'firebase/auth'
import { DEMO_BYPASS_TENANTS } from '@aimsis/shared'
import { configureAuthTenantBeforeSignIn } from '@/lib/auth-tenant'
import { firebaseApp } from '@/lib/firebase'

/**
 * Auth store.
 *
 * State:
 *   - user: the Firebase Auth user (or null)
 *   - role: custom claim ('admin' | 'teacher' | 'proctor' | 'student')
 *   - schoolId: custom claim tenant
 *   - ready: have we received the initial onIdTokenChanged event?
 *
 * The store hooks Firebase's `onIdTokenChanged` so that:
 *   1. Sign-in updates the state automatically
 *   2. Sign-out updates the state automatically
 *   3. Custom claim refreshes (after a server-side claim change) reflect
 *      in role/schoolId on the next token rotation
 *
 * Why setup-style (not options-style):
 *   - Refs compose naturally with other composables (e.g., useNetwork)
 *   - It's the project-mandated pattern
 *   - getters become computed, actions become plain functions
 */

export type Role = 'admin' | 'teacher' | 'proctor' | 'student'
export type ProctorSignInResult = 'signed-in' | 'enrollment-required'
export type ProctorSignInStage = 'enrollment-required' | 'totp-required'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<FbUser | null>(null)
  const role = ref<Role | null>(null)
  const schoolId = ref<string | null>(null)
  const secondFactor = ref<string | null>(null)
  const demoProctorSession = ref(false)
  const mustChangePassword = ref(false)
  const ready = ref(false)
  let pendingProctorCredentials: { email: string; password: string } | null = null
  let pendingTotpSecret: TotpSecret | null = null
  let pendingProctorSecondFactor:
    | { kind: 'resolver'; resolver: MultiFactorResolver; hint: MultiFactorInfo }
    | { kind: 'demo-bypass'; user: FbUser; tokenResult: IdTokenResult }
    | null = null

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() => role.value === 'admin')
  const isTeacher = computed(() => role.value === 'teacher' || role.value === 'admin')
  const isProctor = computed(
    () => role.value === 'proctor' || role.value === 'teacher' || role.value === 'admin',
  )
  const isStudent = computed(() => role.value === 'student')
  const hasProctorMfa = computed(() => secondFactor.value === 'totp')

  function clearAuthState(): void {
    user.value = null
    role.value = null
    schoolId.value = null
    secondFactor.value = null
    demoProctorSession.value = false
    mustChangePassword.value = false
    ready.value = true
  }

  // Hook Firebase Auth state. Runs once at store creation; the unsubscribe
  // function is intentionally not exposed — the store lives for the app's
  // lifetime, so we never need to detach.
  const auth = getAuth(firebaseApp)
  onIdTokenChanged(auth, async (fbUser) => {
    if (fbUser) {
      const tokenResult = await fbUser.getIdTokenResult()
      // Firebase may deliver overlapping initial/sign-in/sign-out callbacks.
      // Never let an older async callback overwrite the SDK's current user.
      if (auth.currentUser?.uid !== fbUser.uid) return
      setAuthState(fbUser, tokenResult)
    } else {
      if (auth.currentUser) return
      clearAuthState()
    }
  })

  async function signIn(email: string, password: string): Promise<void> {
    await configureAuthTenantBeforeSignIn(auth)
    const credential = await signInWithEmailAndPassword(auth, email, password)
    const result = await credential.user.getIdTokenResult()
    // An access-code lock stamps signInLockAt at issue; a password sign-in
    // minted after that is dead at the API (AccessCodeLocked), so cut the
    // session here rather than surfacing a broken sign-in.
    const signInLockAt = result.claims.signInLockAt
    // Compare in seconds — authTime is second-granularity, matching the
    // API gate (a same-second sign-in counts as locked).
    if (
      typeof signInLockAt === 'number'
      && Math.floor(Date.parse(result.authTime) / 1000) >= Math.floor(signInLockAt / 1000)
    ) {
      await fbSignOut(auth)
      clearAuthState()
      throw authError(
        'auth/access-code-locked',
        'This account is locked. Sign in with your student access code or contact your school.',
      )
    }
    if (result.claims.role === 'proctor' && result.signInSecondFactor !== 'totp') {
      await fbSignOut(auth)
      clearAuthState()
      throw authError(
        'auth/proctor-mfa-required',
        'Proctors must use the dedicated TOTP sign-in.',
      )
    }
    setAuthState(credential.user, result)
  }

  async function signInWithCustomToken(customToken: string): Promise<void> {
    await configureAuthTenantBeforeSignIn(auth)
    const credential = await fbSignInWithCustomToken(auth, customToken)
    const result = await credential.user.getIdTokenResult(true)
    setAuthState(credential.user, result)
  }

  /**
   * Proctor sign-in stage one: validate email + password and decide what the
   * UI must ask for next — TOTP enrollment (no factor) or the 6-digit code.
   * The MFA resolver / demo-bypass session needed to finish is stashed in
   * `pendingProctorSecondFactor`; `completeProctorSignIn` consumes it.
   */
  async function beginProctorSignIn(
    email: string,
    password: string,
  ): Promise<ProctorSignInStage> {
    pendingProctorCredentials = null
    pendingTotpSecret = null
    pendingProctorSecondFactor = null
    try {
      await configureAuthTenantBeforeSignIn(auth)
      const credential = await signInWithEmailAndPassword(auth, email, password)
      const result = await verifiedProctorToken(credential.user)
      const demoBypassEligible = isDemoBypassEligible(result)
      if (multiFactor(credential.user).enrolledFactors.length === 0 && !demoBypassEligible) {
        pendingProctorCredentials = { email, password }
        setAuthState(credential.user, result)
        return 'enrollment-required'
      }
      // The demo-bypass session stays signed in while the code step is open —
      // the router guard signs it back out on any fresh navigation anyway.
      pendingProctorSecondFactor = {
        kind: 'demo-bypass',
        user: credential.user,
        tokenResult: result,
      }
      return 'totp-required'
    } catch (err) {
      if ((err as AuthError)?.code !== 'auth/multi-factor-auth-required') throw err
      const resolver = getMultiFactorResolver(auth, err as AuthError)
      const hint = resolver.hints.find(
        (candidate) => candidate.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      )
      if (!hint) {
        throw authError('auth/proctor-totp-not-enrolled', 'A TOTP factor is not enrolled.')
      }
      pendingProctorSecondFactor = { kind: 'resolver', resolver, hint }
      return 'totp-required'
    }
  }

  /**
   * Proctor sign-in stage two: present the 6-digit code for the challenge
   * `beginProctorSignIn` stashed. A wrong code against a real TOTP factor
   * keeps the resolver alive so the code field can retry; a wrong demo-bypass
   * code signs the password-only session out, so the pending state is dropped
   * and the next attempt must start from credentials again.
   */
  async function completeProctorSignIn(totp: string): Promise<'signed-in'> {
    const pending = pendingProctorSecondFactor
    if (!pending) {
      throw authError('auth/proctor-sign-in-required', 'Validate NIP and password first.')
    }
    if (pending.kind === 'resolver') {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(pending.hint.uid, totp)
      try {
        const credential = await pending.resolver.resolveSignIn(assertion)
        pendingProctorSecondFactor = null
        await acceptProctorCredential(credential.user)
        return 'signed-in'
      } catch (err) {
        // A wrong code or a transient failure leaves the pending credential
        // alive — retry in place. Anything else (dead session, disabled user)
        // is not retryable, so drop the challenge and restart at credentials.
        const code = (err as AuthError)?.code
        if (code !== 'auth/invalid-verification-code' && code !== 'auth/network-request-failed') {
          pendingProctorSecondFactor = null
        }
        throw err
      }
    }
    try {
      await acceptProctorCredential(pending.user, totp, pending.tokenResult)
      pendingProctorSecondFactor = null
      return 'signed-in'
    } catch (err) {
      pendingProctorSecondFactor = null
      throw err
    }
  }

  function hasProctorSecondFactorPending(): boolean {
    return pendingProctorSecondFactor !== null
  }

  async function signInProctor(
    email: string,
    password: string,
    totp: string,
  ): Promise<ProctorSignInResult> {
    const stage = await beginProctorSignIn(email, password)
    if (stage === 'enrollment-required') return stage
    await completeProctorSignIn(totp)
    return 'signed-in'
  }

  async function beginProctorTotpEnrollment(): Promise<{
    secretKey: string
    qrCodeUrl: string
  }> {
    const fbUser = auth.currentUser
    if (!fbUser) {
      throw authError('auth/requires-recent-login', 'Sign in again before enrolling TOTP.')
    }
    const factor = multiFactor(fbUser)
    if (factor.enrolledFactors.length > 0) {
      throw authError('auth/proctor-totp-already-enrolled', 'A TOTP factor is already enrolled.')
    }
    const session = await factor.getSession()
    pendingTotpSecret = await TotpMultiFactorGenerator.generateSecret(session)
    return {
      secretKey: pendingTotpSecret.secretKey,
      qrCodeUrl: pendingTotpSecret.generateQrCodeUrl(fbUser.email ?? 'Proctor', 'AIMSIS CBT'),
    }
  }

  async function completeProctorTotpEnrollment(totp: string): Promise<boolean> {
    const fbUser = auth.currentUser
    if (!fbUser || !pendingTotpSecret) {
      throw authError('auth/requires-recent-login', 'Sign in again before enrolling TOTP.')
    }
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(pendingTotpSecret, totp)
    await multiFactor(fbUser).enroll(assertion, 'TOTP')

    const credentials = pendingProctorCredentials
    pendingProctorCredentials = null
    pendingTotpSecret = null
    await fbSignOut(auth)
    clearAuthState()
    if (!credentials) return false

    try {
      return await signInProctor(credentials.email, credentials.password, totp) === 'signed-in'
    } catch (err) {
      // Enrollment already succeeded above; a Firebase auth error here just means the
      // auto-continue sign-in didn't work, so fall back to manual sign-in. Anything else
      // (e.g. a programming error) should surface rather than be silently swallowed.
      if (typeof (err as { code?: string })?.code === 'string') return false
      throw err
    }
  }

  async function signOut(): Promise<void> {
    pendingProctorCredentials = null
    pendingTotpSecret = null
    pendingProctorSecondFactor = null
    try {
      await fbSignOut(auth)
    } finally {
      // Firebase clears its in-memory user first and removes the persisted
      // session second, and that second step throws on storage-blocked
      // browsers. Clearing local state in a `finally` means a rejection there
      // cannot leave a live-looking session behind: `isAuthenticated` would
      // stay true, so the router guard would bounce any navigation away from
      // the page the caller is trying to leave, and a stale
      // mustChangePassword would keep pinning them there. Also runs before
      // onIdTokenChanged has propagated, so the login route guard cannot
      // bounce back.
      clearAuthState()
    }
  }

  /**
   * Force a token refresh — needed after server-side claim changes
   * (e.g., admin elevation) for the new claims to land client-side
   * without waiting up to 1h.
   */
  async function refreshClaims(): Promise<void> {
    if (auth.currentUser) {
      const result = await auth.currentUser.getIdTokenResult(/* forceRefresh */ true)
      setAuthState(auth.currentUser, result)
    }
  }

  return {
    user: readonly(user),
    role: readonly(role),
    schoolId: readonly(schoolId),
    secondFactor: readonly(secondFactor),
    mustChangePassword: readonly(mustChangePassword),
    ready: readonly(ready),
    isAuthenticated,
    isAdmin,
    isTeacher,
    isProctor,
    isStudent,
    hasProctorMfa,
    signIn,
    signInWithCustomToken,
    signInProctor,
    beginProctorSignIn,
    completeProctorSignIn,
    hasProctorSecondFactorPending,
    beginProctorTotpEnrollment,
    completeProctorTotpEnrollment,
    signOut,
    refreshClaims,
  }

  function setAuthState(fbUser: FbUser, tokenResult: IdTokenResult): void {
    user.value = fbUser
    role.value = (tokenResult.claims.role as Role | undefined) ?? null
    schoolId.value = (tokenResult.claims.schoolId as string | undefined) ?? null
    secondFactor.value = tokenResult.signInSecondFactor ?? (demoProctorSession.value ? 'totp' : null)
    mustChangePassword.value = tokenResult.claims.mustChangePassword === true
    ready.value = true
  }

  async function verifiedProctorToken(fbUser: FbUser): Promise<IdTokenResult> {
    const result = await fbUser.getIdTokenResult(true)
    const signedRole = result.claims.role as Role | undefined
    if (!signedRole || !['proctor', 'teacher', 'admin'].includes(signedRole)) {
      await fbSignOut(auth)
      clearAuthState()
      throw authError('auth/not-proctor', 'This account cannot access proctor tools.')
    }
    return result
  }

  function isDemoBypassEligible(result: IdTokenResult): boolean {
    return result.claims.demoProctorBypass === true
      && result.claims.role === 'proctor'
      && typeof result.claims.schoolId === 'string'
      && DEMO_BYPASS_TENANTS.includes(result.claims.schoolId)
  }

  async function acceptProctorCredential(
    fbUser: FbUser,
    presentedTotp?: string,
    tokenResult?: IdTokenResult,
  ): Promise<void> {
    const result = tokenResult ?? await verifiedProctorToken(fbUser)
    const demoBypassEligible = isDemoBypassEligible(result)
    const demoBypassAccepted = demoBypassEligible && presentedTotp === '000000'
    if (result.signInSecondFactor !== 'totp' && !demoBypassAccepted) {
      await fbSignOut(auth)
      clearAuthState()
      if (demoBypassEligible) {
        throw authError('auth/invalid-verification-code', 'The demo TOTP code is incorrect.')
      }
      throw authError(
        'auth/proctor-totp-not-enrolled',
        'Ask an administrator to enroll a TOTP factor for this account.',
      )
    }
    demoProctorSession.value = demoBypassAccepted
    setAuthState(fbUser, result)
  }
})

function authError(code: string, message: string): Error & { code: string } {
  return Object.assign(new Error(message), { code })
}
